import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { sum } from '@proget-shared/helper/rxjs';
import {
  combineLatest,
  distinctUntilKeyChanged,
  EMPTY,
  filter,
  finalize,
  interval,
  map,
  pairwise,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { CountdownTimerPart } from './countdown-timer-part.enum';

@Component({
  selector: 'app-countdown-timer',
  templateUrl: './countdown-timer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CountdownTimerComponent implements OnDestroy {
  @Output()
  public readonly finish = new EventEmitter<void>();

  @Input()
  public display: CountdownTimerPart[] = [CountdownTimerPart.HOURS, CountdownTimerPart.MINUTES, CountdownTimerPart.SECONDS];

  protected time = '-';

  private readonly timestampSubject = new Subject<number>();
  private readonly stopSubject = new Subject<void>();
  private readonly pauseSubject = new Subject<{ paused: boolean; timestamp: number }>();
  private readonly pauseTimeSubject = new Subject<number>();
  private readonly subscription: Subscription;

  constructor(cdr: ChangeDetectorRef) {
    this.subscription = this.timestampSubject.pipe(
      switchMap((finalTimestamp) => (isNaN(finalTimestamp)
        ? EMPTY
        : combineLatest([
          this.pauseSubject
            .pipe(
              startWith({ paused: false, timestamp: Date.now() }),
              distinctUntilKeyChanged('paused'),
              pairwise(),
              map(([previous, current]) => {
                const pauseTime = current.paused ? 0 : Math.max(current.timestamp - previous.timestamp, 0);

                this.pauseTimeSubject.next(pauseTime);

                return current.paused;
              }),
              startWith(false)
            ),
          this.pauseTimeSubject.pipe(
            startWith(0),
            sum()
          ),
          interval(1000).pipe(startWith(0)),
        ]).pipe(
          // pause
          filter(([paused]) => !paused),
          // stop
          takeUntil(this.stopSubject),
          finalize(() => {
            this.time = this.formatDuration(0);
            cdr.markForCheck();
          }),
          // display time
          map(([_, pauseTime]) => Math.max(0, finalTimestamp + pauseTime - Date.now())),
          tap({
            next: (duration) => {
              if (duration === 0) {
                this.stopSubject.next();
                this.finish.emit();
              }
            },
          })
        ))
      )
    )
      .subscribe({
        next: (duration) => {
          this.time = this.formatDuration(duration);
          cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.timestampSubject.complete();
    this.stopSubject.complete();
    this.pauseSubject.complete();
    this.pauseTimeSubject.complete();
  }

  /**
   * @param durationOrDate - Countdown duration in ms or final countdown date
   */
  public start(durationOrDate: number | Date): void {
    const timestamp = durationOrDate instanceof Date
      ? durationOrDate.getTime()
      : Date.now() + durationOrDate;

    this.timestampSubject.next(timestamp);
  }

  public pause(): void {
    this.pauseSubject.next({ paused: true, timestamp: Date.now() });
  }

  public resume({ skipTime } = { skipTime: false }): void {
    this.pauseSubject.next({ paused: false, timestamp: skipTime ? 0 : Date.now() });
  }

  public stop(): void {
    this.stopSubject.next();
  }

  private addLeadingZeroes(n: number): string {
    let stringNumber = n.toString();

    while (stringNumber.length < 2) {
      stringNumber = `0${stringNumber}`;
    }

    return stringNumber;
  }

  private formatDuration(duration: number): string {
    const HOUR = 1000 * 60 * 60;
    const MINUTE = 1000 * 60;
    const SECOND = 1000;
    const parts = [];

    duration = Math.max(0, duration);

    if (this.isPartIncluded(CountdownTimerPart.HOURS)) {
      const hours = Math.floor(duration / HOUR);

      parts.push(hours);
      duration -= hours * HOUR;
    }

    if (this.isPartIncluded(CountdownTimerPart.MINUTES)) {
      const minutes = Math.floor(duration / MINUTE);

      parts.push(minutes);
      duration -= minutes * MINUTE;
    }

    if (this.isPartIncluded(CountdownTimerPart.SECONDS)) {
      parts.push(Math.floor(duration / SECOND));
    }

    return parts.map(this.addLeadingZeroes).join(':');
  }

  private isPartIncluded(part: CountdownTimerPart): boolean {
    return Array.isArray(this.display) && this.display.includes(part);
  }
}
